import {
  Box,
  Button,
  CircularProgress,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import { useDeliveryDetail, useDrinksAndBagsLang } from './hooks';
import { CustomRadio } from '../../../components/common/custom-radio';
import useStyles from './delivery-detail.styles';
import { orderStateType } from '../../../business/order/rt/analytics/core';
import { OrderDeliveryDetailLoader } from './components';
import { Alert } from '@material-ui/lab';
import { ExpandLess } from '@material-ui/icons';
import { StoreAuditAlert } from '../store-audit/components';

export function OrderDeliveryDetail({
  code = '',
  orderId,
  storeId,
  orderState,
  canEditOrderDetailBox = true,
  isMandatory = false,
  isInKanban = false,
  onClose = null,
  isBagQuantitySet = false,
  hasDeliveryDetailMessage,
  hasDefectRate,
}: Props) {
  const {
    quantityOfPackages,
    handleDownQuantityOfPackages,
    handleUpQuantityOfPackages,
    hasDrinksOutside,
    handleHasDrinksOutside,
    isUpdateLoading,
    isUpdateSuccess,
    isUpdateDenied,
    isGetLoading,
    maxQuantityOfPackages,
    isInputMandatory,
    isDisabled,
    handleSubmit,
    isDefectProductsChecked,
  } = useDeliveryDetail({
    code,
    orderId,
    storeId,
    orderState,
    isMandatory,
    isInKanban,
    isBagQuantitySet,
    hasDeliveryDetailMessage,
    hasDefectRate,
  });
  const {
    bagsQuantityText,
    checkCodeText,
    outsideDrinksText,
    detailTitleText,
    kanbanTitleText,
    successText,
    errorText,
    yesText,
    noText,
    completeOrderDetailsPlease,
    confirmOrderDetails,
    required,
  } = useDrinksAndBagsLang();
  const styles = useStyles({ isInKanban });

  const showMandatoryDisclaimer = isInputMandatory && !hasDefectRate;

  if (isGetLoading) return <OrderDeliveryDetailLoader />;

  return (
    <Box
      className={styles.root}
      display='flex'
      flexDirection='column'
      gridGap='0.5rem'
    >
      <Box className={styles.main}>
        <Box
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          marginBottom='1rem'
        >
          <Box className={styles.title}>
            {isInKanban ? kanbanTitleText : detailTitleText}
          </Box>
          {isInKanban ? (
            <ExpandLess onClick={onClose} />
          ) : (
            <>
              {isInputMandatory && (
                <Typography className={styles.mandatory}>{required}</Typography>
              )}
              {(isUpdateSuccess || isBagQuantitySet) && (
                <Box
                  display='flex'
                  justifyContent='flex-start'
                  alignItems='center'
                  gridGap='0.15rem'
                >
                  <Typography className={styles.success}>
                    {successText}
                  </Typography>
                </Box>
              )}
              {isUpdateDenied && (
                <Box
                  display='flex'
                  justifyContent='flex-start'
                  alignItems='center'
                  gridGap='0.15rem'
                >
                  <Typography className={styles.error}>{errorText}</Typography>
                </Box>
              )}
            </>
          )}
        </Box>
        <Box display='flex' flexDirection='column' gridGap='0.75rem'>
          {code && (
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
            >
              <Typography className={styles.checkCodeText}>
                {checkCodeText}
              </Typography>
              <Typography className={styles.checkCode}>{code}</Typography>
            </Box>
          )}
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
          >
            <Typography className={styles.bagsQuantityText}>
              {bagsQuantityText}
            </Typography>
            <Box
              display='flex'
              justifyContent='center'
              alignItems='center'
              gridGap='0.5rem'
            >
              {canEditOrderDetailBox ? (
                <>
                  <Box
                    className={
                      Number.isNaN(quantityOfPackages) ||
                      quantityOfPackages === 1
                        ? styles.quantityButtonDisabled
                        : styles.quantityButton
                    }
                    display='flex'
                    justifyContent='center'
                    alignItems='center'
                    onClick={handleDownQuantityOfPackages}
                  >
                    -
                  </Box>
                  <Typography className={styles.quantityText}>
                    {Number.isNaN(quantityOfPackages)
                      ? '-'
                      : quantityOfPackages}
                  </Typography>
                  <Box
                    className={
                      quantityOfPackages >= maxQuantityOfPackages
                        ? styles.quantityButtonDisabled
                        : styles.quantityButton
                    }
                    display='flex'
                    justifyContent='center'
                    alignItems='center'
                    onClick={handleUpQuantityOfPackages}
                  >
                    +
                  </Box>
                </>
              ) : (
                <Typography className={styles.quantityText}>
                  {Number.isNaN(quantityOfPackages) ? '-' : quantityOfPackages}
                </Typography>
              )}
            </Box>
          </Box>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
          >
            <Typography className={styles.drinksText}>
              {outsideDrinksText}
            </Typography>
            {canEditOrderDetailBox ? (
              <>
                <RadioGroup
                  className={styles.radioGroupOptions}
                  value={hasDrinksOutside}
                >
                  <Box
                    className={
                      hasDrinksOutside
                        ? styles.hasDrinksContainerCheked
                        : styles.hasDrinksContainer
                    }
                  >
                    <CustomRadio
                      classes={{
                        root: styles.radioRootButton,
                        label: hasDrinksOutside
                          ? styles.radioLabelButtonChecked
                          : styles.radioLabelButton,
                      }}
                      color='primary'
                      value={true}
                      onChange={() => handleHasDrinksOutside(true)}
                      label={yesText}
                    />
                  </Box>
                  <Box
                    className={
                      hasDrinksOutside === false
                        ? styles.hasDrinksContainerCheked
                        : styles.hasDrinksContainer
                    }
                  >
                    <CustomRadio
                      classes={{
                        root: styles.radioRootButton,
                        label:
                          hasDrinksOutside === false
                            ? styles.radioLabelButtonChecked
                            : styles.radioLabelButton,
                      }}
                      color='primary'
                      value={false}
                      onChange={() => handleHasDrinksOutside(false)}
                      label={noText}
                    />
                  </Box>
                </RadioGroup>
              </>
            ) : (
              <Typography className={styles.hasDrinksText}>
                {hasDrinksOutside === null
                  ? '-'
                  : hasDrinksOutside
                  ? yesText
                  : noText}
              </Typography>
            )}
          </Box>
          {showMandatoryDisclaimer && (
            <Alert
              severity='info'
              classes={{
                root: styles.alertMandatory,
                message: styles.alertMessage,
              }}
            >
              <span>{completeOrderDetailsPlease}</span>
            </Alert>
          )}
          {hasDefectRate && <StoreAuditAlert orderId={orderId} />}
        </Box>
      </Box>
      <Box className={styles.cta}>
        <Button
          className={styles.submitButton}
          color='primary'
          fullWidth={false}
          variant={isInKanban ? 'contained' : 'outlined'}
          onClick={handleSubmit}
          disabled={isDisabled || isUpdateLoading}
        >
          {isUpdateLoading && (
            <CircularProgress size={15} className={styles.loadingButton} />
          )}
          {confirmOrderDetails}
        </Button>
      </Box>
    </Box>
  );
}

interface Props {
  code?: string;
  orderId: number;
  storeId: number;
  orderState: orderStateType;
  canEditOrderDetailBox?: boolean;
  isMandatory?: boolean;
  hasOrderDeliveryDetailsUpdated?: boolean;
  isInKanban?: boolean;
  onClose?: any;
  isBagQuantitySet?: boolean;
  hasDeliveryDetailMessage: boolean;
  hasDefectRate?: boolean;
}
